import { SPECIES } from '../shared/species.ts';
import { huntPower } from '../shared/power.ts';
import { ELEMENTS } from '../shared/elements.ts';
import { CONFIG } from '../shared/config.ts';
import { ivPercent, qualityPercent } from '../shared/rolls.ts';
import { ratingOf, statWeights } from '../shared/rating.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { computeStats, creatureStats } from '../shared/stats.ts';
import { analyse, type HuntEstimate } from '../shared/analyser.ts';
import type { Fighter } from '../shared/combat.ts';
import type { GroundDef } from '../shared/grounds.ts';
import { TRAIT_BY_ID, speciesTrait } from '../shared/traits.ts';
import { relicPerk } from '../shared/items.ts';
import { keeperMult } from '../shared/halls.ts';
import { S } from './state.ts';
import { displayName, speciesName } from '../shared/nametags.ts';

export const esc = (s: unknown) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
/** The page's number format, made once: toLocaleString() builds a new formatter on every call, and the HUD formats hundreds of numbers each redraw. */
const INT = new Intl.NumberFormat();
export const num = (n: number): string => n < 0 ? `−${num(-n)}` : n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M` : n >= 1e4 ? `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1)}k` : INT.format(Math.round(n) || 0);
/** Never prints -0. */
export const full = (n: number) => INT.format(Math.round(n) || 0);
const BIG_UNITS = ['', 'k', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx'];
/** Damage in a log line: exact below a million, then three figures and a unit (1.23M, 456B, 22.1T), so overkill never runs off the line. */
export function big(n: number): string {
  if (Math.abs(n) < 1e6) return full(n);
  let a = Math.abs(n), u = 0;
  while (a >= 999.5 && u < BIG_UNITS.length - 1) { a /= 1000; u++; }
  return `${n < 0 ? '−' : ''}${a >= 99.95 ? a.toFixed(0) : a >= 9.995 ? a.toFixed(1) : a.toFixed(2)}${BIG_UNITS[u]}`;
}
/** Quality as its effect on stats: ×1.072 reads +7%, ×0.98 reads −2%. */
export const qualityEffect = (q: number) => { const p = Math.round((q - 1) * 100); return p > 0 ? `+${p}%` : p < 0 ? `−${-p}%` : '±0%'; };

export function elChip(e: string) {
  const el = ELEMENTS[e as keyof typeof ELEMENTS];
  return `<span class="el ${esc(e)}" style="background:${el.color}">${el.name}</span>`;
}

export function ratingClass(r: number) { return r >= 50 ? 'r-hi' : r >= 30 ? 'r-mid' : 'r-lo'; }

/** A creature's name as plain text: its nickname, else its species name (shared/nametags.ts displayName). */
export function cname(c: CreatureDTO) { return displayName(c); }
/** A creature's name as markup: a nickname with the species as quiet secondary text, else the species name. */
export function nameHtml(c: CreatureDTO) {
  return c.nickname ? `${esc(c.nickname)} <span class="nick-sp">${esc(speciesName(c))}</span>` : esc(speciesName(c));
}

export function summary(c: CreatureDTO) {
  const rating = ratingOf(c.rolls, c.species);
  const st = creatureStats(c);
  return { rating, power: huntPower(c), stats: st, iv: ivPercent(c.rolls, statWeights(c.species)), quality: qualityPercent(c.rolls), bloodline: CONFIG.bloodline[c.rolls.bloodline].name };
}

export function ago(ts: number) {
  const s = Math.max(0, (Date.now() - ts) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export function left(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  // Weekly quests, the arena season and 30-day VIP read in days, not "136h".
  if (s >= 48 * 3600) return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

/** "~4 min", "about 2 h 10 min"; never raw seconds above 90. */
export function duration(ms: number) {
  const s = Math.round(ms / 1000);
  if (!isFinite(s) || s < 0) return '—';
  if (s <= 90) return `~${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `~${m} min`;
  return `about ${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
}

/** halls: the trainer's kept halls (TrainerDTO.halls), so the hunt estimate includes the Hall Keeper bonus as the server's hunt does. */
export function fighterOf(c: CreatureDTO, halls?: readonly string[]): Fighter {
  const hallMult = keeperMult(c.species, halls);
  return { speciesId: c.species, level: c.level, stats: creatureStats(c), trait: speciesTrait(c.rolls.trait, c.species), ...(hallMult !== 1 ? { hallMult } : {}), ...(c.tablet ? { tablet: c.tablet } : {}) };
}

const estimates = new Map<string, HuntEstimate>();
/** The analyser's estimate for a creature leading on a hunt, cached until it levels or gains a star. */
export function estimate(c: CreatureDTO, g: GroundDef): HuntEstimate {
  const halls = S.trainer?.halls;
  const key = `${c.id}:${c.species}:${c.level}:${c.stars}:${c.refine?.join('.') ?? ''}:${c.train?.join('.') ?? ''}:${c.tablet ?? ''}:${g.id}:${keeperMult(c.species, halls)}`;
  let e = estimates.get(key);
  if (!e) {
    if (estimates.size > 2000) estimates.clear();
    e = analyse(fighterOf(c, halls), g);
    estimates.set(key, e);
  }
  return e;
}

/** A team-wide multiplier as the server's hunt computes it: the team's traits multiply, and its best relic of the kind counts. */
export function teamPerk(kind: 'drop' | 'catch' | 'xp' | 'coin', team: CreatureDTO[]): number {
  let m = 1;
  for (const c of team) {
    const t = TRAIT_BY_ID[c.rolls.trait];
    const tm = kind === 'drop' ? t?.dropMult : kind === 'catch' ? t?.catchMult : kind === 'xp' ? t?.xpMult : t?.coinMult;
    if (tm) m *= tm;
  }
  return m * relicPerk(team.map((c) => c.relic), kind);
}

export const RISK_TEXT ={ safe: 'Safe', ok: 'Some risk', risky: 'Risky' } as const;
export const RISK_CLASS = { safe: 'risk-safe', ok: 'risk-ok', risky: 'risk-risky' } as const;
